import { ConfirmationDialog, PageSpinner } from '@armhr/ui';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Link as MUILink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { useApiData } from '../../hooks/useApiData';
import { paths } from '../../utils/paths';

const RehireListPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [confirmEmployee, setConfirmEmployee] =
    useState<TerminatedEmployee | null>(null);

  const { data: employees, loading } = useApiData<TerminatedEmployee[]>((api) =>
    api.onboarding.getTerminatedEmployees()
  );

  const filtered = useMemo(() => {
    if (!employees) return [];
    if (!search.trim()) return employees;
    const q = search.toLowerCase();
    return employees.filter(
      (e) =>
        `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
        (e.work_email && e.work_email.toLowerCase().includes(q))
    );
  }, [employees, search]);

  const handleRehireClick = useCallback((employee: TerminatedEmployee) => {
    setConfirmEmployee(employee);
  }, []);

  const handleConfirm = useCallback(() => {
    if (confirmEmployee) {
      navigate(`${paths.rehireSetup}/${confirmEmployee.employee_id}`);
    }
    setConfirmEmployee(null);
  }, [confirmEmployee, navigate]);

  const handleCancelConfirm = useCallback(() => {
    setConfirmEmployee(null);
  }, []);

  if (loading) return <PageSpinner />;

  return (
    <>
      <Helmet>
        <title>Rehire Employee | Armhr</title>
      </Helmet>

      <MUILink
        component={RouterLink}
        to={paths.newHires}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}
        underline="hover"
      >
        <ArrowBackIcon fontSize="small" />
        Back to New Hire Dashboard
      </MUILink>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h2" mb={0.5}>
            Rehire Employee
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select a previously terminated employee to begin the rehire process.
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Employee name</TableCell>
              <TableCell>Email address</TableCell>
              <TableCell>Termination date</TableCell>
              <TableCell>Department</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary" py={4}>
                    {search
                      ? 'No terminated employees match your search.'
                      : 'No terminated employees found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((employee) => {
                const doNotRehire =
                  (employee.rehire_ok || '').toUpperCase() === 'N';
                return (
                  <TableRow key={employee.employee_id}>
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography variant="body2">
                          {employee.first_name} {employee.last_name}
                        </Typography>
                        {doNotRehire && (
                          <Tooltip title="This employee is marked as Do Not Rehire in PrismHR. Rehiring requires an explicit override.">
                            <Chip
                              label="Do not rehire"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{employee.work_email || '—'}</TableCell>
                    <TableCell>{employee.termination_date || '—'}</TableCell>
                    <TableCell>{employee.department || '—'}</TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleRehireClick(employee)}
                      >
                        Rehire
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmationDialog
        open={!!confirmEmployee}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirm}
        title="Confirm rehire"
        message={
          <>
            You are about to start the rehire process for{' '}
            <strong>
              {confirmEmployee?.first_name} {confirmEmployee?.last_name}
            </strong>
            . This will open the full hire setup form with their personal
            details pre-filled.
          </>
        }
        confirmText="Continue"
        cancelText="Cancel"
      />
    </>
  );
};

export default RehireListPage;
